import React, { useEffect, useState } from "react";
import {
    MessageCircle,
    Sparkles,
    ArrowRight,
    Lightbulb,
    Search,
    ChevronLeft,
    ChevronRight,
    Send,
} from "lucide-react";

/* Conversazioni demo per il carosello a destra. */
const CONVERSATIONS = [
    {
        title: "Hai uno scavo da fare?",
        messages: [
            {
                role: "user",
                text: "Devo scavare una buca di 2 metri per una fondazione, cosa mi consigli?",
            },
            {
                role: "assistant",
                text: "Per uno scavo di 2 m ti suggerisco il Miniescavatore q.li 25/30 (€100/giorno): profondità di scavo ~2,8 m e maneggevolezza ottima per cantieri medi. Vuoi aggiungerlo al preventivo?",
            },
        ],
    },
    {
        title: "Hai un muro da demolire?",
        messages: [
            {
                role: "user",
                text: "Ho un muro in cemento armato da abbattere. Cosa mi serve?",
            },
            {
                role: "assistant",
                text: "Ti consiglio il Martello demolitore q.li 25/30 (€50/giorno) montato su escavatore, oppure la Pinza demolitrice q.li 140 (€240/giorno) per lavori più pesanti. Quanto è grande il muro?",
            },
        ],
    },
    {
        title: "Ti serve corrente in cantiere?",
        messages: [
            {
                role: "user",
                text: "Mi serve corrente per saldare e accendere 4 utensili contemporaneamente.",
            },
            {
                role: "assistant",
                text: "Perfetto, il Gruppo elettrogeno 14/18 kVA (€50/giorno) è la scelta giusta: silenzioso, affidabile e dimensionato per saldatrici + utensileria. Per quanti giorni?",
            },
        ],
    },
    {
        title: "Vuoi sapere gli sconti attivi?",
        messages: [
            { role: "user", text: "Avete promozioni o codici sconto in corso?" },
            {
                role: "assistant",
                text: "Sì! BENVENUTO10 (-10% per nuovi clienti), CANTIERE15 (-15% sopra €1.000) e NSP2026 (-5% sempre). Applicabili nel calcolatore preventivo.",
            },
        ],
    },
    {
        title: "Quanto costa la consegna?",
        messages: [
            { role: "user", text: "Il trasporto del mezzo è incluso?" },
            {
                role: "assistant",
                text: "Il trasporto è preventivabile a parte in base a distanza e tipo di mezzo. Per la zona Rieti/Sabina la consegna è veloce. Dimmi la località e ti do una stima.",
            },
        ],
    },
];

export const ChatCTA = () => {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => {
            setActive((prev) => (prev + 1) % CONVERSATIONS.length);
        }, 5500);
        return () => clearInterval(id);
    }, [paused]);

    const goTo = (i) =>
        setActive(
            ((i % CONVERSATIONS.length) + CONVERSATIONS.length) %
                CONVERSATIONS.length
        );

    const openChat = () => {
        document.querySelector('[data-testid="chat-trigger"]')?.click();
    };

    return (
        <section
            id="chatbot-cta"
            data-testid="chat-cta-section"
            className="relative bg-nsp-green-dark text-white py-24 lg:py-28 overflow-hidden"
        >
            <div className="absolute inset-0 industrial-grid-bg opacity-10" />

            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 relative">
                {/* HEADER */}
                <div className="max-w-3xl mb-14 lg:mb-20">
                    <div className="inline-flex items-center gap-2 bg-nsp-yellow text-nsp-green px-3 py-1.5 mb-6 text-xs font-bold uppercase tracking-[0.3em]">
                        <Sparkles className="w-3 h-3" /> Il nostro assistente sempre con te
                    </div>
                    <h2
                        data-testid="chat-cta-title"
                        className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tighter"
                    >
                        Non sai
                        <br />
                        <span className="text-nsp-yellow">cosa scegliere?</span>
                    </h2>
                    <p className="text-white/70 text-lg mt-6 max-w-xl leading-relaxed">
                        Il nostro <b className="text-white">assistente virtuale</b> ti aiuta
                        a trovare il mezzo giusto in base alle esigenze del tuo cantiere.
                        Disponibile 24/7, parla italiano, conosce a memoria l'intero listino.
                    </p>
                </div>

                {/* GRID: photo with overlays + chat slideshow */}
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start">
                    {/* LEFT: Worker photo with overlays */}
                    <div className="lg:col-span-5 relative">
                        <WorkerPhotoCard />
                    </div>

                    {/* RIGHT: Chat slideshow */}
                    <div className="lg:col-span-7">
                        <div
                            data-testid="chat-cta-slideshow"
                            role="button"
                            tabIndex={0}
                            onClick={openChat}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openChat();
                                }
                            }}
                            onMouseEnter={() => setPaused(true)}
                            onMouseLeave={() => setPaused(false)}
                            className="bg-white text-zinc-800 rounded-3xl shadow-2xl cursor-pointer group hover:shadow-[0_25px_60px_-15px_rgba(255,209,0,0.35)] transition-shadow overflow-hidden"
                            aria-label="Apri la chat con l'assistente NSP"
                        >
                            <div className="flex items-center justify-between px-5 py-3 bg-nsp-bg border-b border-nsp-border">
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1.5">
                                        <span className="w-3 h-3 rounded-full bg-red-400" />
                                        <span className="w-3 h-3 rounded-full bg-yellow-400" />
                                        <span className="w-3 h-3 rounded-full bg-green-400" />
                                    </div>
                                    <span className="ml-3 text-xs text-zinc-500 font-bold uppercase tracking-wider">
                                        assistente.nsp
                                    </span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-nsp-green font-bold flex items-center gap-2 group-hover:text-nsp-green-dark">
                                    <span className="hidden sm:inline">Clicca per chattare</span>
                                    <span className="sm:hidden">Tocca per chattare</span>
                                    <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                                </div>
                            </div>

                            <div className="relative min-h-[300px] sm:min-h-[280px]">
                                {CONVERSATIONS.map((conv, idx) => (
                                    <div
                                        key={idx}
                                        data-testid={`chat-cta-slide-${idx}`}
                                        className={`absolute inset-0 p-6 transition-all duration-500 ${
                                            idx === active
                                                ? "opacity-100 translate-y-0 pointer-events-auto"
                                                : "opacity-0 translate-y-2 pointer-events-none"
                                        }`}
                                    >
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-nsp-green font-bold mb-4">
                                            ▸ {conv.title}
                                        </div>
                                        <div className="space-y-3">
                                            {conv.messages.map((m, i) => (
                                                <ChatBubble key={i} role={m.role}>
                                                    {m.text}
                                                </ChatBubble>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center justify-between border-t border-nsp-border bg-nsp-bg px-5 py-4">
                                <button
                                    data-testid="chat-cta-prev"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        goTo(active - 1);
                                    }}
                                    className="w-9 h-9 bg-white border border-nsp-border text-nsp-green hover:bg-nsp-green hover:text-nsp-yellow flex items-center justify-center transition-colors"
                                    aria-label="Conversazione precedente"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <div className="flex gap-2">
                                    {CONVERSATIONS.map((_, i) => (
                                        <button
                                            key={i}
                                            data-testid={`chat-cta-dot-${i}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                goTo(i);
                                            }}
                                            className={`h-1.5 transition-all ${
                                                i === active
                                                    ? "w-8 bg-nsp-green"
                                                    : "w-3 bg-nsp-border hover:bg-zinc-400"
                                            }`}
                                            aria-label={`Vai alla conversazione ${i + 1}`}
                                        />
                                    ))}
                                </div>
                                <button
                                    data-testid="chat-cta-next"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        goTo(active + 1);
                                    }}
                                    className="w-9 h-9 bg-white border border-nsp-border text-nsp-green hover:bg-nsp-green hover:text-nsp-yellow flex items-center justify-center transition-colors"
                                    aria-label="Conversazione successiva"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className="mt-6 grid sm:grid-cols-3 gap-4">
                            <Feature
                                icon={Lightbulb}
                                title="Consiglio tecnico"
                                text="Suggerimenti su misura"
                            />
                            <Feature
                                icon={Search}
                                title="Confronta i mezzi"
                                text="Prezzi e specifiche"
                            />
                            <Feature
                                icon={Sparkles}
                                title="Risposte istantanee"
                                text="24/7, in italiano"
                            />
                        </div>

                        <button
                            data-testid="chat-cta-open"
                            onClick={openChat}
                            className="mt-8 w-full sm:w-auto rounded-full bg-nsp-yellow text-nsp-green px-8 py-5 font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-3 hover:bg-white transition-colors shadow-md active:scale-95"
                        >
                            <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                            Avvia la chat ora
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* =========================================================
 * WorkerPhotoCard: foto operaio + 3 overlay
 *   - badge "CHAT IN TEMPO REALE" (in alto a sinistra)
 *   - callout giallo con domanda (in alto a destra)
 *   - phone mockup stilizzato (in basso a destra)
 * ========================================================= */
const WorkerPhotoCard = () => {
    return (
        <div data-testid="worker-photo-card" className="relative">
            {/* Photo + phone mockup contained within */}
            <div className="relative aspect-[3/4] overflow-hidden bg-nsp-green-dark">
                <img
                    src="https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=900&q=85"
                    alt="Operaio NSP in cantiere"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-nsp-green-dark/40 via-transparent to-transparent" />

                {/* Top-left: chat badge */}
                <div
                    data-testid="chat-badge-overlay"
                    className="absolute top-4 left-4 bg-nsp-green/95 backdrop-blur text-nsp-yellow px-3 py-2 text-[10px] font-bold uppercase tracking-[0.25em] flex items-center gap-2 border-b-2 border-nsp-yellow z-10"
                >
                    <span className="relative flex w-2 h-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-nsp-yellow opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-nsp-yellow" />
                    </span>
                    Chat in tempo reale
                </div>

                {/* Bottom-right: Phone mockup INSIDE the photo container with green circle accent */}
                <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 w-36 sm:w-40 lg:w-44 z-10">
                    {/* Green circle backdrop */}
                    <div
                        aria-hidden="true"
                        className="absolute -inset-4 lg:-inset-6 bg-gradient-to-br from-nsp-green-bright to-nsp-green-vivid rounded-full opacity-90"
                    />
                    <div
                        aria-hidden="true"
                        className="absolute -inset-2 lg:-inset-3 bg-nsp-yellow rounded-full opacity-30 blur-md"
                    />
                    <PhoneMockup />
                </div>
            </div>

            {/* Below-photo: Yellow callout (outside photo, no overflow) */}
            <div
                data-testid="chat-callout-overlay"
                className="relative -mt-8 ml-4 mr-4 sm:ml-8 sm:mr-8 max-w-[320px] bg-nsp-yellow text-nsp-green p-4 sm:p-5 rounded-2xl shadow-xl z-20"
            >
                <p className="text-sm leading-snug">
                    Hai dubbi su quale sia il <b>miglior equipaggiamento</b> per portare a
                    termine il tuo lavoro?
                </p>
                <div className="mt-3 pt-3 border-t border-nsp-green/20 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
                    <span className="font-bold uppercase tracking-wider text-sm">
                        Chatta con noi 24/7
                    </span>
                </div>
            </div>
        </div>
    );
};

/* Stilized iPhone-like mockup with mini chat */
const PhoneMockup = () => {
    return (
        <div
            data-testid="phone-mockup"
            className="relative bg-zinc-900 rounded-[2rem] p-1.5 shadow-2xl ring-1 ring-zinc-700"
        >
            {/* Inner screen */}
            <div className="bg-white rounded-[1.6rem] overflow-hidden">
                {/* Notch */}
                <div className="relative bg-zinc-900 h-5 flex items-center justify-center">
                    <div className="w-14 h-3 bg-zinc-950 rounded-b-xl" />
                </div>
                {/* Status bar */}
                <div className="bg-white px-3 py-1 flex items-center justify-between text-[8px] font-bold text-zinc-700">
                    <span>10:15</span>
                    <span className="flex gap-0.5">
                        <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                        <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                        <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                    </span>
                </div>
                {/* Chat header */}
                <div className="bg-nsp-green text-white px-3 py-2 flex items-center gap-2">
                    <div className="w-6 h-6 bg-nsp-yellow flex items-center justify-center">
                        <Sparkles className="w-3 h-3 text-nsp-green" strokeWidth={3} />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider">
                            NSP Chat
                        </div>
                        <div className="text-[8px] text-nsp-yellow flex items-center gap-1">
                            <span className="w-1 h-1 bg-nsp-yellow rounded-full animate-pulse" />
                            Online
                        </div>
                    </div>
                </div>

                {/* Chat body */}
                <div className="bg-nsp-bg p-2 space-y-2 min-h-[140px]">
                    {/* User bubble */}
                    <div className="flex justify-end">
                        <div className="bg-nsp-green text-white text-[8px] leading-tight px-2 py-1.5 rounded-2xl rounded-tr-sm max-w-[80%]">
                            Devo scavare 2m per una fondazione, cosa mi consigli?
                        </div>
                    </div>
                    {/* Assistant bubble */}
                    <div className="flex justify-start gap-1">
                        <div className="w-4 h-4 bg-nsp-green flex-shrink-0 flex items-center justify-center mt-0.5">
                            <span className="text-nsp-yellow text-[6px] font-bold">NSP</span>
                        </div>
                        <div className="bg-white border border-nsp-border text-zinc-700 text-[8px] leading-tight px-2 py-1.5 rounded-2xl rounded-tl-sm max-w-[85%]">
                            Per uno scavo di 2m ti suggerisco il Miniescavatore q.li 25/30
                            (€100/giorno). Vuoi aggiungerlo al preventivo?
                        </div>
                    </div>
                </div>

                {/* Input bar */}
                <div className="bg-white border-t border-nsp-border px-2 py-1.5 flex items-center gap-1.5">
                    <div className="flex-1 bg-nsp-bg rounded-full px-2 py-1 text-[8px] text-zinc-400">
                        Scrivi un messaggio...
                    </div>
                    <div className="w-5 h-5 bg-nsp-green flex items-center justify-center rounded-full">
                        <Send className="w-2.5 h-2.5 text-nsp-yellow" />
                    </div>
                </div>
            </div>
            {/* Home indicator */}
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-white/40 rounded-full" />
        </div>
    );
};

const ChatBubble = ({ role, children }) => (
    <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
        <div
            className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed rounded-2xl ${
                role === "user"
                    ? "bg-nsp-green text-white rounded-tr-md"
                    : "bg-nsp-bg border border-nsp-border rounded-tl-md"
            }`}
        >
            {children}
        </div>
    </div>
);

const Feature = ({ icon: Icon, title, text }) => (
    <div className="border-l-2 border-nsp-yellow pl-4">
        <Icon className="w-6 h-6 text-nsp-yellow mb-2" strokeWidth={2} />
        <div className="font-bold uppercase tracking-wide text-sm">{title}</div>
        <div className="text-white/60 text-xs mt-1">{text}</div>
    </div>
);

export default ChatCTA;
